import { injectBackend } from '@/app/backend_client';
import type { MipController } from '@/features/mip/shared/mip.controller.server';
import type { BuildingId } from '@/interfaces/Building.interface';
import { BrowserProvider } from '@/providers/browser.provider';
import { InformationProvider } from '@/providers/information/information.provider';
import { AdvizeoLoadingService } from '@/services/advizeo-loading.service';
import { injectToastErrorHandler } from '@/utils/errors/toast-error-handler.utils';
import { I18nService } from '@/utils/i18n/i18n.service';
import type { PageResponse } from '@/utils/pagination/pagination';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { computed, inject, Injectable, linkedSignal, resource, signal, type TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import type { MipActionDetail, MipActionSummary, MipScenariosGeneralInfo } from '../mip-scenario/mip-scenario.interface';
import type { MipStandardAction } from '../mip-standard-action/mip-standard-action.interface';
import type { MipActionId, MipInvestmentPlanId, MipScenarioId } from '../shared/mip-ids.interface';
import type { MipScenarioActionDetailResponse } from './mip-scenario.interface';

/**
 * MipScenarioDataService
 *
 * A Frontend state management service for scenario creation workflow(No API interaction).
 * This service manages the data of 1)general information and 2)standard actions,
 * allowed the state to be shared between different components involved in the scenario creation and generation process.
 */
@Injectable({
    providedIn: 'root'
})
export class MipScenarioDataService {
    private _mipController = injectBackend<MipController>().mip;
    private _informationProvider = inject(InformationProvider);
    private _browserProvider = inject(BrowserProvider);
    private _loadingService = inject(AdvizeoLoadingService);
    private _i18nService = inject(I18nService);

    private _investmentPlanId = signal<MipInvestmentPlanId | null>(null);
    private _scenarioId = signal<MipScenarioId | null>(null);
    private _scenarioGeneralInfo = signal<MipScenariosGeneralInfo>({ name: '', description: undefined });
    private _toastError = injectToastErrorHandler();

    private _inFlightMutations = new Set<Promise<any>>();
    private _hasUncomputedChanges = false;

    readonly _actionListResource = resource<
        MipActionSummary[],
        { investmentPlanId: MipInvestmentPlanId; scenarioId: MipScenarioId } | undefined
    >({
        params: () => {
            const investmentPlanId = this._investmentPlanId();
            const scenarioId = this._scenarioId();
            if (investmentPlanId != null && scenarioId != null) {
                return { investmentPlanId, scenarioId };
            }
            return undefined;
        },
        loader: ({ params }) =>
            this._mipController.scenarioAction.getScenarioActionsSummary.query(params).then(summaries =>
                summaries.map(summary => ({
                    mipActionId: summary.mipActionId,
                    buildingCount: summary.buildingNumber,
                    standardAction: {
                        actionCategory: summary.actionCategory,
                        standardActionId: summary.standardActionId,
                        actionKey: summary.actionKey,
                        theoreticalEnergyBenefit: summary.defaultTheoreticalEnergyBenefit,
                        theoreticalBenefitPreference: summary.defaultTheoreticalBenefitPreference,
                        theoreticalCost: summary.defaultTheoreticalCost,
                        theoreticalCostPreference: summary.defaultTheoreticalCostPreference
                    }
                }))
            )
    });

    private _scenarioActions = linkedSignal({
        source: this._actionListResource.value,
        computation: (loadedActions: MipActionSummary[] | undefined, previous: { value: MipActionSummary[] } | undefined) =>
            loadedActions ?? previous?.value ?? []
    });

    readonly scenarioActionsDetailsDataChanged$ = new Subject<void>();

    private _plan = resource({
        params: () => ({ investmentPlanId: this._investmentPlanId() }),
        loader: async ({ params: { investmentPlanId } }) => {
            if (!investmentPlanId) {
                return undefined;
            }
            return this._mipController.plan.getPlan.query(investmentPlanId);
        }
    });

    private _scenario = resource({
        params: () => ({ scenarioId: this._scenarioId(), investmentPlanId: this._investmentPlanId() }),
        loader: async ({ params: { scenarioId, investmentPlanId } }) => {
            if (!scenarioId || !investmentPlanId) {
                return undefined;
            }
            return this._mipController.scenario.getScenario.query({ investmentPlanId: investmentPlanId, scenarioId: scenarioId });
        }
    });

    private _isGeneralInfoValid = linkedSignal({
        source: this._scenario.value,
        computation: (scenario, previous): boolean => {
            if (isNotNullish(scenario)) {
                return scenario.name.trim().length > 0;
            }
            return previous?.value ?? false;
        }
    });

    actionButtonTemplate = signal<TemplateRef<void> | null>(null);

    readonly scenarioActions = this._scenarioActions.asReadonly();
    readonly isGeneralInfoValid = this._isGeneralInfoValid.asReadonly();
    readonly allActionsHaveBuilding = computed(() => this.scenarioActions().every(action => action.buildingCount > 0));
    readonly isScenarioCreationValid = computed(() => {
        const actions = this.scenarioActions();
        return this._scenario.hasValue() && isNotNullish(this._scenario.value()) && actions.length > 0 && this.allActionsHaveBuilding();
    });
    readonly scenarioGeneralInfo = this._scenarioGeneralInfo.asReadonly();
    readonly plan = this._plan.value.asReadonly();
    readonly scenario = this._scenario.value.asReadonly();

    updateGeneralInfo(info: MipScenariosGeneralInfo, isValid: boolean) {
        this._scenarioGeneralInfo.set(info);
        this._isGeneralInfoValid.set(isValid);
    }

    async checkGeneralInfoValid(investmentPlanId: MipInvestmentPlanId, scenarioId: MipScenarioId): Promise<boolean> {
        if (this._isGeneralInfoValid()) {
            return true;
        }
        const scenario = await this._mipController.scenario.getScenario.query({ investmentPlanId, scenarioId });
        return scenario.name.trim().length > 0;
    }

    addScenarioActions(actions: MipStandardAction[]) {
        const scenarioActions = this._scenarioActions();
        const existingStandardActionIds = scenarioActions.map(scenarioAction => scenarioAction.standardAction.standardActionId);

        const standardActionsToCreate: MipStandardAction[] = actions.filter(
            standardAction => !existingStandardActionIds.includes(standardAction.standardActionId)
        );

        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();

        if (isNotNullish(investmentPlanId) && isNotNullish(scenarioId)) {
            this._mipController.scenarioAction.addActionsToScenario
                .mutate({
                    investmentPlanId,
                    scenarioId,
                    standardActionIds: standardActionsToCreate.map(standardAction => standardAction.standardActionId)
                })
                .then(() => {
                    this._actionListResource.reload();
                });
        }
    }

    async importBulkEdit(file: File): Promise<boolean> {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNullish(investmentPlanId) || isNullish(scenarioId)) {
            return false;
        }

        const formData = new FormData();
        formData.append('investmentPlanId', String(investmentPlanId));
        formData.append('scenarioId', String(scenarioId));
        formData.append('file', file);

        const spinner = this._loadingService.show(this._i18nService.translateR('STUFF.LOADING'));
        try {
            const { resultFileUrl, hasErrors } = await this._mipController.scenarioAction.importScenarioActionsBulkEdit
                .mutate(formData)
                .catch(this._toastError);
            this._informationProvider.showToast(
                this._i18nService.translateR(
                    hasErrors ? 'FEATURES.MIP.SCENARIO.BULK_EDIT.IMPORT.HAS_ERRORS' : 'FEATURES.MIP.SCENARIO.BULK_EDIT.IMPORT.SUCCESS'
                ),
                hasErrors ? 'error' : 'success'
            );

            this._actionListResource.reload();
            this._browserProvider.downloadFile(resultFileUrl);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            spinner.dismiss();
        }
    }

    getBulkEditTemplate(): Promise<string> {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNullish(investmentPlanId) || isNullish(scenarioId)) {
            return Promise.reject(new Error('Missing investment plan or scenario context'));
        }

        return this._mipController.scenarioAction.getScenarioActionsTemplateFile.query({ investmentPlanId, scenarioId });
    }

    reset() {
        this._isGeneralInfoValid.set(false);
        this._scenarioGeneralInfo.set({ name: '', description: undefined });
        this._scenarioActions.set([]);
        this._investmentPlanId.set(null);
        this._scenarioId.set(null);
    }

    updatePlanIdAndScenarioId(investmentPlanId: MipInvestmentPlanId, scenarioId: MipScenarioId | null) {
        this._investmentPlanId.set(investmentPlanId);
        this._scenarioId.set(scenarioId);
    }

    deleteAction(mipActionId: MipActionId) {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNotNullish(investmentPlanId) && isNotNullish(scenarioId)) {
            this._trackMutation(
                this._mipController.scenarioAction.removeActionFromScenario.mutate({ investmentPlanId, scenarioId, mipActionId })
            );
        }

        this._scenarioActions.update(actions => actions.filter(action => action.mipActionId !== mipActionId));
    }

    addBuildingsToAction(mipActionId: MipActionId, actionDetails: MipActionDetail[]) {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNotNullish(investmentPlanId) && isNotNullish(scenarioId)) {
            this._trackMutation(
                this._mipController.scenarioAction.addBuildingsToScenarioAction.mutate({
                    investmentPlanId,
                    scenarioId,
                    mipActionId,
                    actionDetails
                })
            ).then(() => {
                const actionBuildingCount = new Set(actionDetails.map(d => d.buildingId)).size;
                this._scenarioActions.update(actions =>
                    actions.map(a => (a.mipActionId === mipActionId ? { ...a, buildingCount: a.buildingCount + actionBuildingCount } : a))
                );
                this.scenarioActionsDetailsDataChanged$.next();
            });
        }
    }

    updateScenarioGeneralInfo() {
        if (
            this._scenario.hasValue() &&
            (this._scenario.value().name !== this._scenarioGeneralInfo().name ||
                this._scenario.value().description !== this._scenarioGeneralInfo().description)
        ) {
            this._mipController.scenario.updateScenario
                .mutate({
                    investmentPlanId: this._scenario.value().planId,
                    scenarioId: this._scenario.value().scenarioId,
                    payload: {
                        name: this._scenarioGeneralInfo().name,
                        description: this._scenarioGeneralInfo().description
                    }
                })
                .then(() => {
                    this._informationProvider.showToast(
                        'FEATURES.MIP.SCENARIO.MIP_SCENARIO_CONFIGURATION.UPDATE_SCENARIO_SUCCESS',
                        'success'
                    );
                    this._scenario.reload();
                })
                .catch(() => {
                    this._informationProvider.showToast('FEATURES.MIP.SCENARIO.MIP_SCENARIO_CONFIGURATION.UPDATE_SCENARIO_FAILED', 'error');
                });
        }
    }

    removeBuildingFromAction(mipActionId: MipActionId, buildingId: BuildingId) {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNotNullish(investmentPlanId) && isNotNullish(scenarioId)) {
            this._trackMutation(
                this._mipController.scenarioAction.deleteScenarioActionBuilding.mutate({
                    investmentPlanId,
                    scenarioId,
                    mipActionId,
                    buildingId
                })
            ).then(() => {
                this._scenarioActions.update(actions =>
                    actions.map(a => (a.mipActionId === mipActionId ? { ...a, buildingCount: Math.max(0, a.buildingCount - 1) } : a))
                );
                this.scenarioActionsDetailsDataChanged$.next();
            });
        }
    }

    removeDeliveryPointFromAction(mipActionId: MipActionId, buildingId: BuildingId, updatedActionDetail: MipActionDetail) {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNotNullish(investmentPlanId) && isNotNullish(scenarioId)) {
            this._trackMutation(
                this._mipController.scenarioAction.updateScenarioActionBuilding.mutate({
                    investmentPlanId,
                    scenarioId,
                    mipActionId,
                    buildingId,
                    actionDetail: updatedActionDetail
                })
            ).then(() => {
                this.scenarioActionsDetailsDataChanged$.next();
            });
        }
    }

    /**
     * Persists an inline edit of a building detail row (startYear, theoreticalEnergyBenefit, theoreticalCost).
     * Does NOT trigger scenarioActionsDetailsDataChanged$ — the local display already shows the updated value.
     */
    updateBuildingDetail(mipActionId: MipActionId, buildingId: BuildingId, actionDetail: MipActionDetail) {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();
        if (isNotNullish(investmentPlanId) && isNotNullish(scenarioId)) {
            this._trackMutation(
                this._mipController.scenarioAction.updateScenarioActionBuilding.mutate({
                    investmentPlanId,
                    scenarioId,
                    mipActionId,
                    buildingId,
                    actionDetail
                })
            );
        }
    }

    /**
     * Fetches paginated scenario action details from the API.
     * Used by the component to create PaginatedResourceSignals per action.
     */
    fetchScenarioActionDetails(
        mipActionId: MipActionId,
        page: number,
        size: number
    ): Promise<PageResponse<MipScenarioActionDetailResponse>> {
        const investmentPlanId = this._investmentPlanId();
        const scenarioId = this._scenarioId();

        if (isNullish(investmentPlanId) || isNullish(scenarioId)) {
            return Promise.resolve({ content: [], totalElements: 0, last: true });
        }

        return this._mipController.scenarioAction.getScenarioActionDetail.query({
            investmentPlanId,
            scenarioId,
            mipActionId,
            page,
            size
        });
    }

    /**
     * Triggers a backend recompute of the savings — only if something actually changed since the last recompute —
     * once all in-flight mutations have settled, so the recomputed values reflect the latest edits.
     * Called when the user leaves the scenario edition/synthesis pages.
     * @returns `true` if a recompute was actually performed, `false` if there was nothing to recompute.
     */
    async triggerRecompute(): Promise<boolean> {
        const investmentPlanId = this._investmentPlanId();
        if (isNullish(investmentPlanId)) {
            return false;
        }
        await this._settlePendingMutations();
        if (!this._hasUncomputedChanges) {
            return false;
        }
        try {
            await this._mipController.plan.saveCompute.mutate({ investmentPlanId });
            this._hasUncomputedChanges = false;
            return true;
        } catch (error) {
            console.error('Failed to trigger MIP savings recompute', error);
            return false;
        }
    }

    private _trackMutation<T>(promise: Promise<T>): Promise<T> {
        this._hasUncomputedChanges = true;
        this._inFlightMutations.add(promise);
        void promise.catch(() => {}).finally(() => this._inFlightMutations.delete(promise));
        return promise;
    }

    private async _settlePendingMutations(): Promise<void> {
        await Promise.allSettled([...this._inFlightMutations]);
    }
}
